/**
 * Task #1316 — Guard riusabile per il cambio chat model che invaliderebbe
 * documenti multimodali Gemini già indicizzati nella KB di un agente.
 *
 * Uso (in un modale di configurazione agente):
 *
 *   const guard = useKbModelChangeGuard();
 *
 *   const handleSave = async () => {
 *     // ...validazioni...
 *     await guard.runGuardedSave({
 *       agentId: isEdit ? agent.id : null,
 *       newChatModel: formData.model,
 *       performSave: ({ reingestDocIds }) => doActualSave(reingestDocIds),
 *     });
 *   };
 *
 *   // ...nel JSX...
 *   //   <KbModelChangeGuardDialog state={guard.state} />
 *
 * Comportamento:
 *  - Se `agentId` è null (creazione) → esegue subito performSave senza dialog.
 *  - Altrimenti chiama capabilities; se cambio invalida ≥1 doc multimodale,
 *    apre dialog con 3 azioni: Annulla / Salva senza re-ingest /
 *    Salva e re-ingesta N doc.
 *  - Bulk re-ingest viene eseguito DOPO il performSave per garantire che il
 *    worker veda già il provider aggiornato.
 */
import { useState, useCallback, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@easyflow/ui-frontend/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import {
  checkChatModelChangeImpact,
  bulkReingestDocuments,
  invalidateCapabilitiesCache,
} from '@/hooks/useKBCapabilities';

export interface KbGuardedPerformSaveArgs {
  reingestDocIds: ReadonlyArray<string> | null;
}

export type KbGuardedPerformSave = (
  args: KbGuardedPerformSaveArgs
) => Promise<void>;

export interface RunGuardedSaveArgs {
  agentId: string | null | undefined;
  newChatModel: string | null | undefined;
  performSave: KbGuardedPerformSave;
}

export interface KbImpactedDoc {
  id: string;
  name: string;
}

interface PendingSave {
  agentId: string;
  multimodalDocsCount: number;
  multimodalDocs: ReadonlyArray<KbImpactedDoc>;
  performSave: KbGuardedPerformSave;
}

export interface KbModelChangeGuardState {
  open: boolean;
  multimodalDocsCount: number;
  multimodalDocs: ReadonlyArray<KbImpactedDoc>;
  reingestRunning: boolean;
  onCancel: () => void;
  onSaveWithoutReingest: () => Promise<void>;
  onSaveAndReingest: () => Promise<void>;
}

export interface UseKbModelChangeGuardResult {
  state: KbModelChangeGuardState;
  runGuardedSave: (args: RunGuardedSaveArgs) => Promise<void>;
}

export function useKbModelChangeGuard(): UseKbModelChangeGuardResult {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [reingestRunning, setReingestRunning] = useState(false);
  const pendingRef = useRef<PendingSave | null>(null);

  const runReingest = useCallback(
    async (agentId: string, ids: ReadonlyArray<string>): Promise<void> => {
      if (ids.length === 0) return;
      setReingestRunning(true);
      try {
        const report = await bulkReingestDocuments(agentId, ids);
        toast({
          title: report.failed === 0 ? 'Re-ingest avviato' : 'Re-ingest parziale',
          description: `${report.succeeded}/${report.total} documenti accodati.${report.failed > 0 ? ` ${report.failed} falliti.` : ''}`,
          variant: report.failed === 0 ? 'default' : 'destructive',
        });
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'errore sconosciuto';
        toast({ title: 'Re-ingest fallito', description: msg, variant: 'destructive' });
      } finally {
        setReingestRunning(false);
      }
    },
    [toast]
  );

  const runGuardedSave = useCallback(
    async (args: RunGuardedSaveArgs): Promise<void> => {
      const { agentId, newChatModel, performSave } = args;
      if (!agentId) {
        await performSave({ reingestDocIds: null });
        return;
      }
      try {
        const impact = await checkChatModelChangeImpact(agentId, newChatModel);
        if (impact.willInvalidate) {
          pendingRef.current = {
            agentId,
            multimodalDocsCount: impact.multimodalDocsCount,
            multimodalDocs: impact.multimodalDocs,
            performSave,
          };
          setOpen(true);
          return;
        }
      } catch {
        // Capabilities non disponibili (agent appena creato, tenant non
        // configurato, ecc.): non blocchiamo il salvataggio.
      }
      await performSave({ reingestDocIds: null });
      invalidateCapabilitiesCache(agentId);
    },
    []
  );

  const onCancel = useCallback(() => {
    if (reingestRunning) return;
    pendingRef.current = null;
    setOpen(false);
  }, [reingestRunning]);

  const onSaveWithoutReingest = useCallback(async () => {
    const pending = pendingRef.current;
    if (!pending) {
      setOpen(false);
      return;
    }
    pendingRef.current = null;
    setOpen(false);
    await pending.performSave({ reingestDocIds: null });
    invalidateCapabilitiesCache(pending.agentId);
  }, []);

  const onSaveAndReingest = useCallback(async () => {
    const pending = pendingRef.current;
    if (!pending) {
      setOpen(false);
      return;
    }
    const { agentId, multimodalDocs, performSave } = pending;
    const ids = multimodalDocs.map((d) => d.id);
    pendingRef.current = null;
    setOpen(false);
    await performSave({ reingestDocIds: ids });
    await runReingest(agentId, ids);
  }, [runReingest]);

  const pending = pendingRef.current;
  const state: KbModelChangeGuardState = {
    open,
    multimodalDocsCount: pending?.multimodalDocsCount ?? 0,
    multimodalDocs: pending?.multimodalDocs ?? [],
    reingestRunning,
    onCancel,
    onSaveWithoutReingest,
    onSaveAndReingest,
  };

  return { state, runGuardedSave };
}

interface KbModelChangeGuardDialogProps {
  state: KbModelChangeGuardState;
  /** Tailwind classes opzionali per il pulsante primario "Salva e re-ingesta". */
  primaryButtonClassName?: string;
}

export function KbModelChangeGuardDialog({
  state,
  primaryButtonClassName,
}: KbModelChangeGuardDialogProps): JSX.Element {
  const {
    open,
    multimodalDocsCount,
    multimodalDocs,
    reingestRunning,
    onCancel,
    onSaveWithoutReingest,
    onSaveAndReingest,
  } = state;

  const PREVIEW_LIMIT = 50;
  const visibleDocs = multimodalDocs.slice(0, PREVIEW_LIMIT);
  const hiddenCount = Math.max(0, multimodalDocs.length - visibleDocs.length);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && !reingestRunning) onCancel();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cambio chat model — documenti multimodali a rischio</DialogTitle>
          <DialogDescription>
            Sono presenti <b>{multimodalDocsCount}</b> documento/i indicizzato/i in modalità
            multimodale Gemini. Salvando con un modello non-Gemini non saranno più consultabili
            finché non verranno re-ingestati col provider corrente.
          </DialogDescription>
        </DialogHeader>
        {visibleDocs.length > 0 ? (
          <div className="rounded-md border bg-muted/40 px-3 py-2 max-h-56 overflow-y-auto">
            <p className="text-xs font-medium text-muted-foreground mb-1">
              Documenti impattati:
            </p>
            <ul className="text-sm space-y-0.5">
              {visibleDocs.map((d) => (
                <li key={d.id} className="truncate" title={d.name}>
                  • {d.name}
                </li>
              ))}
            </ul>
            {hiddenCount > 0 ? (
              <p className="text-xs text-muted-foreground mt-1">
                …e altri {hiddenCount} documento/i.
              </p>
            ) : null}
          </div>
        ) : null}
        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button variant="outline" disabled={reingestRunning} onClick={onCancel}>
            Annulla
          </Button>
          <Button
            variant="outline"
            disabled={reingestRunning}
            onClick={() => {
              void onSaveWithoutReingest();
            }}
          >
            Salva senza re-ingest
          </Button>
          <Button
            disabled={reingestRunning}
            className={primaryButtonClassName}
            onClick={() => {
              void onSaveAndReingest();
            }}
          >
            {reingestRunning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Salva e re-ingesta {multimodalDocsCount} doc
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
